import { useMemo, Children, isValidElement } from "react";
import { useField } from "formik";
import cn from "classnames";

import { Typography } from "shared/ui/components/Typography";

import styles from "./FieldGroup.module.scss";

const FieldGroup = ({
  className,
  labelClassName,
  errorClassName,
  label,
  children,
  hidden,
}: {
  className?: string;
  labelClassName?: string;
  errorClassName?: string;
  hidden: boolean;
  label: string;
  children: React.ReactNode;
}) => {
  const fieldNames = useMemo(() => {
    const names: string[] = [];
    Children.forEach(children, (child) => {
      if (isValidElement(child)) {
        const childProps = child.props as any;
        if (childProps?.name) {
          names.push(childProps.name);
        }
      }
    });
    return names;
  }, [children]);

  const fieldStates = fieldNames.map((name) => {
    try {
      const [, meta] = useField(name);
      return meta;
    } catch {
      return null;
    }
  });

  const { hasError, errorMessages } = useMemo(() => {
    let hasError = false;
    const errors: string[] = [];

    fieldStates.forEach((meta) => {
      if (meta && meta.touched && meta.error) {
        hasError = true;
        errors.push(meta.error);
      }
    });

    return { hasError, errorMessages: errors.join(" | ") };
  }, [fieldStates]);

  if (hidden) {
    return null;
  }

  return (
    <>
      <div className={cn(styles.fieldGroup, { [styles.fieldGroupError]: hasError }, className)}>
        <Typography
          variant="bodyM"
          weight="regular"
          className={cn(styles.fieldGroupLabel, labelClassName)}
        >
          {label}
        </Typography>
        {children}
      </div>
      {hasError && errorMessages && (
        <div className={cn(styles.error, errorClassName)}>
          <Typography className={styles.errorText} variant="bodyXS">
            {errorMessages}
          </Typography>
        </div>
      )}
    </>
  );
};

export default FieldGroup;
